import { cleanText } from "@/lib/eventFoundation";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Minus, Plus, Ticket, CalendarDays, MapPin, ShoppingBag, CreditCard } from "lucide-react";
import { toast } from "sonner";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useProfile } from "@/hooks/platform/useProfile";
import { useTicketTypes } from "@/hooks/platform/useEvents";
import { useCart } from "@/hooks/platform/useCart";
import { dashboardPath } from "@/lib/dashboardPaths";
import { useCurrentUser } from "@/hooks/platform/useCurrentUser";
import { Loader2 } from "lucide-react";

export interface TicketTarget {
  eventId: string;
  ownerId?: string | null;
  title: string;
  flyerUrl?: string | null;
  when?: string | null;
  where?: string | null;
  /** Showcase events pass their tiers directly — nothing is charged for samples. */
  sampleTiers?: { id: string; name: string; price: number; description?: string }[];
}

interface Props {
  target: TicketTarget | null;
  onClose: () => void;
}

/** "Get tickets" pop-up: pick tiers and quantities, then add to cart or go straight to checkout. */
const TicketPurchaseDialog = ({ target, onClose }: Props) => {
  const isSample = !!target?.sampleTiers;
  const { data: liveTypes = [], isLoading } = useTicketTypes(isSample ? null : target?.eventId ?? null);
  const { add, checkout } = useCart();
  const { user, userId } = useCurrentUser();
  const [paying, setPaying] = useState(false);
  const navigate = useNavigate();
  const [qty, setQty] = useState<Record<string, number>>({});

  // Age-restricted events (e.g. 18+ licensed venues) require a verified date of birth.
  const { data: profileData } = useProfile();
  const { data: minAge = null } = useQuery({
    queryKey: ["event-min-age", target?.eventId],
    enabled: !!target?.eventId && !isSample,
    queryFn: async () => {
      const { data } = await supabase.from("events").select("min_age").eq("id", target!.eventId).maybeSingle();
      return (data?.min_age as number | null) ?? null;
    },
  });
  const ageBlocked = !!minAge && minAge >= 18 && !profileData?.profile?.age_verified;

  const tiers = useMemo(() => {
    if (target?.sampleTiers) {
      return target.sampleTiers.map((t) => ({ id: t.id, name: t.name, description: t.description ?? "", price: t.price, left: 50, soldOut: false }));
    }
    return liveTypes
      .filter((t) => t.is_active)
      .map((t) => ({
        id: t.id,
        name: t.name,
        description: cleanText(t.description),
        price: Number(t.price),
        left: Math.max(t.quantity_total - t.quantity_sold, 0),
        soldOut: t.quantity_total > 0 && t.quantity_sold >= t.quantity_total,
      }));
  }, [target, liveTypes]);

  const total = tiers.reduce((s, t) => s + (qty[t.id] ?? 0) * t.price, 0);
  const picked = tiers.reduce((s, t) => s + (qty[t.id] ?? 0), 0);

  if (!target) return null;

  const bump = (id: string, delta: number, max: number) =>
    setQty((q) => ({ ...q, [id]: Math.max(0, Math.min(max || 99, (q[id] ?? 0) + delta)) }));

  const buildLines = () => tiers.filter((t) => (qty[t.id] ?? 0) > 0).map((t) => ({
    itemType: "ticket" as const,
    ticketTypeId: t.id,
    eventId: target.eventId,
    sellerId: target.ownerId ?? null,
    title: `${target.title} — ${t.name}`,
    unitPrice: t.price,
    quantity: qty[t.id] ?? 0,
    image: target.flyerUrl ?? null,
  }));

  const payNow = async () => {
    if (isSample) { toast.info("Sample event — real events issue QR tickets at checkout."); return; }
    if (!picked) { toast.error("Choose at least one ticket"); return; }
    if (ageBlocked) { toast.error(`This event is ${minAge}+. Add your date of birth in Settings to verify your age.`); return; }
    if (!userId) { navigate(`/auth?next=${encodeURIComponent(`/events?event=${target.eventId}`)}`); return; }
    setPaying(true);
    try {
      const { authorizationUrl } = await checkout(userId, user?.email ?? null, buildLines() as never);
      localStorage.setItem("mcg_tickets_path", dashboardPath("tickets"));
      toast.success("Opening secure payment…");
      window.location.href = authorizationUrl;
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not start payment");
      setPaying(false);
    }
  };

  const addAll = () => {
    if (isSample) { toast.info("Sample event — real events issue QR tickets at checkout."); return false; }
    if (!picked) { toast.error("Choose at least one ticket"); return false; }
    if (ageBlocked) {
      toast.error(`This event is ${minAge}+. Add your date of birth in Settings to verify your age.`);
      return false;
    }
    tiers.forEach((t) => {
      const n = qty[t.id] ?? 0;
      if (!n) return;
      add({
        itemType: "ticket",
        ticketTypeId: t.id,
        eventId: target.eventId,
        sellerId: target.ownerId ?? null,
        title: `${target.title} — ${t.name}`,
        unitPrice: t.price,
        quantity: n,
        image: target.flyerUrl ?? null,
      });
    });
    return true;
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-gray-950 border-gray-800 text-white max-w-lg max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg"><Ticket className="w-4 h-4 text-purple-400" />Get tickets</DialogTitle>
        </DialogHeader>

        {ageBlocked && (
          <p className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-300">
            This event is {minAge}+. Verify your date of birth in Settings → Verification information before checkout.
          </p>
        )}

        <div className="space-y-1">
          <p className="font-semibold">{target.title}</p>
          <div className="flex flex-wrap gap-3 text-xs text-gray-400">
            {target.when && <span className="flex items-center gap-1"><CalendarDays className="w-3.5 h-3.5" />{target.when}</span>}
            {target.where && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{target.where}</span>}
            {isSample && <Badge variant="outline" className="border-amber-600/50 text-amber-300 text-[10px]">Sample</Badge>}
          </div>
        </div>

        {isLoading ? (
          <p className="text-sm text-gray-500">Loading ticket tiers…</p>
        ) : tiers.length === 0 ? (
          <p className="text-sm text-gray-500">The organiser hasn't put tickets on sale for this event yet.</p>
        ) : (
          <div className="space-y-2">
            {tiers.map((t) => (
              <div key={t.id} className="flex items-center justify-between gap-3 rounded-xl border border-gray-800 px-4 py-3">
                <div className="min-w-0">
                  <p className="font-medium truncate">{t.name}</p>
                  <p className="text-xs text-gray-500 truncate">{t.description || `${t.left} remaining`}</p>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="font-semibold">R{t.price.toFixed(0)}</span>
                  <div className="flex items-center gap-1">
                    <Button size="icon" variant="ghost" className="h-7 w-7" aria-label={`Fewer ${t.name}`} disabled={t.soldOut} onClick={() => bump(t.id, -1, t.left)}><Minus className="w-3.5 h-3.5" /></Button>
                    <span className="w-5 text-center text-sm tabular-nums">{qty[t.id] ?? 0}</span>
                    <Button size="icon" variant="ghost" className="h-7 w-7" aria-label={`More ${t.name}`} disabled={t.soldOut} onClick={() => bump(t.id, 1, t.left)}><Plus className="w-3.5 h-3.5" /></Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="flex items-center justify-between border-t border-gray-800 pt-3 text-sm">
          <span className="text-gray-400">{picked} ticket{picked === 1 ? "" : "s"}</span>
          <span className="font-bold">R{total.toFixed(0)}</span>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button
            className="flex-1 gap-2"
            onClick={payNow}
            disabled={paying}
          >
            {paying ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}Pay R{total.toFixed(0)} & get tickets
          </Button>
          <Button
            variant="outline"
            className="gap-2"
            onClick={() => { if (addAll()) { toast.success("Added to cart"); onClose(); } }}
          >
            <ShoppingBag className="w-4 h-4" />Add to cart
          </Button>
        </div>
        <p className="text-[11px] text-gray-600">Payment is taken securely at checkout. Your QR ticket appears in My Tickets the moment payment clears, ready to download.</p>
      </DialogContent>
    </Dialog>
  );
};

export default TicketPurchaseDialog;
